'use client';

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Card, CardBody, Button, Progress, addToast } from '@heroui/react';
import { Typography } from '../../../../../components';
import { InfoIcon } from '../../../../../components/icons';
import clientAccService from '../../../../../api/client/clientAccService';
import CloseIcon from './close.svg';
import AddPhotoIcon from './add-photo-icon.svg';
import { showCreatorSuccess } from '../../../../../utils/toast';
import { useTranslations } from '../../../../../contexts/translation.context';

interface PublicContentTabProps {
  modelId?: string;
  existingImages?: string[];
}

interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  isUploading: boolean;
  uploadProgress: number;
  error?: string;
}

interface ExistingImage {
  id: string;
  url: string;
  mediaId: string; // Реальный ID медиа с сервера
  isExisting: true;
  isDeleting?: boolean; // Состояние удаления
}

const PublicContentTab: React.FC<PublicContentTabProps> = ({ modelId, existingImages = [] }) => {
  const { t } = useTranslations();
  // Очищаем текст от литеральных символов "\n" (не перевод строки, а сам текст)
  const infoDescription = useMemo(() => {
    const raw = t('accountPage.publicContent.info.description');
    return typeof raw === 'string' ? raw.replace(/\\n/g, ' ') : raw;
  }, [t]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [existingImagesState, setExistingImagesState] = useState<ExistingImage[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Инициализируем существующие изображения
  useEffect(() => {
    const existing = existingImages.map((url, index) => {
      // Извлекаем ID из URL (последняя часть после слеша)
      const mediaId = url.split('/').pop() || `media-${index}`;

      // Логируем для отладки
      console.log(`Изображение ${index}: URL=${url}, извлеченный ID=${mediaId}`);

      return {
        id: `existing-${index}`,
        url,
        mediaId, // Реальный ID медиа из URL
        isExisting: true as const,
      };
    });
    setExistingImagesState(existing);
  }, [existingImages]);

  const uploadFile = useCallback(
    async (fileData: UploadedFile) => {
      if (!modelId) {
        console.error('ModelId не найден для загрузки файла');
        return;
      }

      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id
            ? { ...f, isUploading: true, uploadProgress: 0, error: undefined }
            : f
        )
      );

      let progressInterval: NodeJS.Timeout | undefined;

      try {
        const formData = new FormData();
        formData.append('file', fileData.file);

        // Simulate upload progress
        progressInterval = setInterval(() => {
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.id === fileData.id
                ? { ...f, uploadProgress: Math.min(f.uploadProgress + 10, 90) }
                : f
            )
          );
        }, 200);

        // Отправляем файл через API
        const response = await clientAccService.publicMedia(modelId, formData);
        console.log('Файл успешно загружен:', response);

        clearInterval(progressInterval);

        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id ? { ...f, isUploading: false, uploadProgress: 100 } : f
          )
        );

        // Показываем toast уведомление об успешной загрузке
        // addToast({
        //   title: "Успешно!",
        //   description: `Файл "${fileData.file.name}" успешно загружен`,
        //   color: "success",
        //   timeout: 3000,
        // });
        showCreatorSuccess(
          t('accountPage.publicContent.success.fileUploaded', { fileName: fileData.file.name })
        );
      } catch (error: any) {
        console.error('Ошибка при загрузке файла:', error);

        if (progressInterval) {
          clearInterval(progressInterval);
        }

        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id
              ? {
                  ...f,
                  isUploading: false,
                  error:
                    error?.response?.data?.detail ||
                    t('accountPage.publicContent.errors.uploadError'),
                }
              : f
          )
        );
      }
    },
    [modelId]
  );

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files) return;

      const maxFileSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const errors: string[] = [];

      const validFiles = Array.from(files).filter((file) => {
        if (!allowedTypes.includes(file.type)) {
          errors.push(
            t('accountPage.publicContent.errors.unsupportedFormat', { fileName: file.name })
          );
          return false;
        }
        if (file.size > maxFileSize) {
          errors.push(
            t('accountPage.publicContent.errors.fileTooLarge', {
              fileName: file.name,
              size: (file.size / 1024 / 1024).toFixed(1),
            })
          );
          return false;
        }
        return true;
      });

      // Check if adding these files would exceed the limit (including existing images)
      const totalImages = existingImagesState.length + uploadedFiles.length + validFiles.length;
      if (totalImages > 6) {
        errors.push(
          t('accountPage.publicContent.errors.maxFiles', {
            existing: existingImagesState.length + uploadedFiles.length,
          })
        );
      }

      // Показываем ошибки если есть
      if (errors.length > 0) {
        setValidationErrors(errors);
        // Очищаем ошибки через 5 секунд
        setTimeout(() => {
          setValidationErrors([]);
        }, 5000);
        return;
      }

      // Очищаем ошибки если все файлы валидны
      setValidationErrors([]);

      const newFiles: UploadedFile[] = validFiles.map((file) => ({
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: URL.createObjectURL(file),
        isUploading: false,
        uploadProgress: 0,
      }));

      setUploadedFiles((prev) => [...prev, ...newFiles]);

      // Автоматически начинаем загрузку новых файлов
      newFiles.forEach((file) => uploadFile(file));
    },
    [uploadedFiles.length, uploadFile, existingImagesState.length]
  );

  const removeFile = (fileId: string) => {
    setUploadedFiles((prev) => {
      const file = prev.find((f) => f.id === fileId);
      if (file) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  };

  const removeExistingImage = async (mediaId: string) => {
    if (!modelId) {
      console.error('ModelId не найден для удаления изображения');
      return;
    }

    try {
      // Находим изображение по mediaId
      const image = existingImagesState.find((img) => img.mediaId === mediaId);
      if (!image) {
        console.error('Изображение не найдено');
        return;
      }

      console.log(`Удаляем изображение: URL=${image.url}, mediaId=${image.mediaId}`);

      // Устанавливаем состояние удаления
      setExistingImagesState((prev) =>
        prev.map((img) => (img.mediaId === mediaId ? { ...img, isDeleting: true } : img))
      );

      // Вызываем API для удаления изображения
      await clientAccService.deletePublicMedia(modelId, image.mediaId);

      // Если удаление прошло успешно, убираем изображение из состояния
      setExistingImagesState((prev) => prev.filter((img) => img.mediaId !== mediaId));

      console.log('Изображение успешно удалено');
    } catch (error: any) {
      console.error('Ошибка при удалении изображения:', error);

      // Сбрасываем состояние удаления при ошибке
      setExistingImagesState((prev) =>
        prev.map((img) => (img.mediaId === mediaId ? { ...img, isDeleting: false } : img))
      );

      // Показываем ошибку пользователю
      setValidationErrors((prev) => [
        ...prev,
        `Ошибка при удалении изображения: ${error?.response?.data?.detail || 'Неизвестная ошибка'}`,
      ]);

      // Очищаем ошибки через 5 секунд
      setTimeout(() => {
        setValidationErrors([]);
      }, 5000);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (e.target) {
      e.target.value = '';
    }
  };

  const totalImages = existingImagesState.length + uploadedFiles.length;
  const canUploadMore = totalImages < 6;
  const hasAnyImages = totalImages > 0;

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      uploadedFiles.forEach((file) => {
        URL.revokeObjectURL(file.preview);
      });
    };
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <Typography variant="h6">{t('accountPage.publicContent.title')}</Typography>

      {/* Information Block */}
      <Card className="border border-l-[0.1875rem] border-l-partner rounded-none shadow-sm bg-gray-98">
        <CardBody className="p-4">
          <div className="flex gap-4">
            <div className="icon size-8 md:size-9">
              <InfoIcon />
            </div>
            <div>
              <Typography variant="body2" className="!text-partner font-medium leading-5 mb-0.5">
                {t('accountPage.publicContent.info.title')}
              </Typography>
              <Typography variant="body2" className="text-auth-muted leading-5">
                {infoDescription}
              </Typography>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Card className="border border-red-200 bg-red-50">
          <CardBody className="p-4">
            {validationErrors.map((error, index) => (
              <Typography key={index} variant="body2" className="text-red-600 mb-1">
                {error}
              </Typography>
            ))}
          </CardBody>
        </Card>
      )}

      {/* File Upload Block */}
      <Card className="border border-gray-90 bg-gray-98 rounded-xl shadow-sm">
        <CardBody className="p-4">
          {/* Upload Area or File Grid */}
          {!hasAnyImages ? (
            <div
              className={`h-[6.25rem] border-2 border-dashed rounded-lg flex items-center justify-center transition-colors ${
                isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="text-center">
                <Typography variant="body2" className="text-auth-muted mb-4">
                  {t('accountPage.publicContent.upload.dragText')}
                  <span
                    className="text-partner font-semibold cursor-pointer ml-1"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t('accountPage.publicContent.upload.selectText')}
                  </span>
                </Typography>
                <Typography variant="body3" className="text-auth-muted text-xs">
                  {t('accountPage.publicContent.upload.supportedFormats')}
                </Typography>
              </div>
            </div>
          ) : (
            <div className="flex gap-4 flex-wrap">
              {/* Existing Images */}
              {existingImagesState.map((image) => (
                <div key={image.id} className="relative group size-[6.25rem]">
                  <div className="relative size-[6.25rem] rounded-xl overflow-hidden bg-gray-100">
                    <img
                      src={image.url}
                      alt="Existing content"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Remove Button for existing images */}
                  <button
                    onClick={() => removeExistingImage(image.mediaId)}
                    disabled={image.isDeleting}
                    className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                      image.isDeleting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <img src={CloseIcon} className="w-5 h-5" alt="Remove" />
                  </button>

                  {/* Loading Overlay for deleting */}
                  {image.isDeleting && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <Progress
                        value={0} // Placeholder for progress, will be updated by API
                        className="w-16 h-2"
                        color="primary"
                      />
                      <Typography variant="body3" className="text-white mt-1 text-xs">
                        {t('accountPage.publicContent.deleting')}
                      </Typography>
                    </div>
                  )}
                </div>
              ))}

              {/* New Uploaded Files */}
              {uploadedFiles.map((file) => (
                <div key={file.id} className="relative group size-[6.25rem]">
                  <div className="relative size-[6.25rem] rounded-xl overflow-hidden bg-gray-100">
                    <img
                      src={file.preview}
                      alt="New upload"
                      className="w-full h-full object-cover"
                    />

                    {/* Upload Progress Overlay */}
                    {file.isUploading && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="text-center">
                          <Progress
                            value={file.uploadProgress}
                            className="w-16 h-2"
                            color="primary"
                          />
                          <Typography variant="body3" className="text-white mt-1 text-xs">
                            {file.uploadProgress}%
                          </Typography>
                        </div>
                      </div>
                    )}

                    {/* Error Overlay */}
                    {file.error && (
                      <div className="absolute inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center">
                        <div className="text-center">
                          <Typography variant="body3" className="text-white text-xs">
                            {t('accountPage.publicContent.error')}
                          </Typography>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Remove Button for new files */}
                  <button
                    onClick={() => removeFile(file.id)}
                    className="absolute -top-2.5 -right-2.5 w-5 h-5 rounded-full flex items-center justify-center"
                  >
                    <img src={CloseIcon} className="w-5 h-5" alt="Remove" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </CardBody>
      </Card>

      {/* Add Photo Button */}
      {hasAnyImages && canUploadMore && (
        <Button
          color="primary"
          className="rounded-xl h-10 w-fit"
          startContent={<img src={AddPhotoIcon} className="w-5 h-5" alt="Add" />}
          onPress={() => fileInputRef.current?.click()}
        >
          {t('accountPage.publicContent.addPhoto')}
        </Button>
      )}
    </div>
  );
};

export default PublicContentTab;
